import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import * as authApi from "@/api/auth";
import { tokenStore } from "@/api/client";
import type { AuthRole } from "@/api/client";
import { clearAllSticky } from "@/hooks/useStickyState";
import { clearRecentScreens } from "@/hooks/useRecentScreens";
import { PORTAL_ROLES } from "@/types/domain";
import type {
  AppRole,
  AuthDriver,
  AuthUser,
  DriverLoginRequest,
  LoginRequest,
  MeResponse,
  PortalRole,
  TokenResponse,
  UserRole,
} from "@/types/domain";

/**
 * 인증 상태 컨텍스트.
 *
 * - 세션의 원천은 서버다. 앱 부팅 시 저장된 토큰으로 `GET /api/auth/me` 를 호출해
 *   주체 정보를 복원하고, 복원 중에는 `isRestoring` 으로 화면을 잡아 둔다.
 *   (토큰만 보고 인증됐다고 단정하지 않는다 — 만료·비활성 계정을 걸러야 한다)
 * - 주체 정보는 TanStack Query 가 보유하며 컨텍스트는 이를 그대로 노출한다.
 *   컴포넌트 로컬 state 로 복사하지 않는다.
 * - 401(재발급까지 실패)이면 토큰을 비우고 미인증으로 떨어뜨린다. 어디로 보낼지는
 *   라우트 가드(`RequireAuth`)가 결정한다.
 */

/**
 * `users.role` → 토큰 슬롯. 슬롯은 **만료 시 돌아갈 로그인 화면**을 가리킨다.
 *
 * **역할을 더하면 이 표에도 더한다.** 빠뜨리면 `admin` 으로 저장돼 그 거래처가 만료
 * 때마다 담당자 로그인 화면으로 튕기고, 거기서는 아무리 맞게 쳐도 들어갈 수 없다.
 * 표로 둔 것은 삼항 연산자를 역할마다 한 겹 더 쌓지 않으려는 것이다.
 */
const ROLE_SLOT: Partial<Record<UserRole, AuthRole>> = {
  업체: "carrier",
  청구업체: "client",
};

function slotForRole(role: UserRole | undefined): AuthRole {
  return (role && ROLE_SLOT[role]) || "admin";
}

export const ME_QUERY_KEY = ["auth", "me"] as const;

export interface AuthContextValue {
  /** 담당자·시스템관리자 (기사 로그인이면 null) */
  user: AuthUser | null;
  /** 기사 (관리자 로그인이면 null) */
  driver: AuthDriver | null;
  /** `시스템관리자` | `담당자` | `기사`. 미인증이면 null */
  role: AppRole | null;
  /**
   * 포털 계정이 **쓸 수 있는 기능 전부**. 포털 계정이 아니면 빈 배열.
   *
   * 한 계정이 운송업체·청구업체 기능을 함께 켤 수 있다(고객 질문 2026-10-15). `role`
   * 은 대표 하나뿐이라 **탭·라우트 가드는 이 목록을 봐야 한다** — 그러지 않으면 둘 다
   * 켠 계정이 한쪽 포털에서 *접근 권한이 없습니다* 를 본다.
   */
  portalRoles: PortalRole[];
  /** 헤더에 표시할 로그인 주체 이름 */
  displayName: string;
  isAuthenticated: boolean;
  /** 저장된 토큰으로 세션을 복원하는 중 */
  isRestoring: boolean;
  login: (payload: LoginRequest) => Promise<TokenResponse>;
  driverLogin: (payload: DriverLoginRequest) => Promise<TokenResponse>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** 저장된 토큰 유무. 로그인·로그아웃 때만 바뀌는 클라이언트 상태다. */
function hasStoredToken(): boolean {
  return Boolean(tokenStore.getAccess());
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [tokenPresent, setTokenPresent] = useState<boolean>(hasStoredToken);

  const meQuery = useQuery<MeResponse>({
    queryKey: ME_QUERY_KEY,
    queryFn: authApi.getMe,
    enabled: tokenPresent,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // 토큰이 있는데 /me 가 401·403 이면 세션이 끝난 것이다. 토큰을 비우고 미인증으로 떨어뜨린다.
  const meError = meQuery.error;
  useEffect(() => {
    if (!meError || !axios.isAxiosError(meError)) return;
    const status = meError.response?.status;
    if (status === 401 || status === 403) {
      tokenStore.clear();
      setTokenPresent(false);
    }
  }, [meError]);

  /** 로그인 응답을 토큰 저장 + 캐시 반영으로 확정한다. */
  const applyToken = useCallback(
    (token: TokenResponse) => {
      tokenStore.set(
        token.access_token,
        token.refresh_token,
        // 포털 역할은 `actor_type` 이 user 라 **역할까지 봐야 갈린다** — 만료 시
        // 돌아갈 로그인 화면이 담당자와 다르기 때문이다.
        token.actor_type === "driver" ? "driver" : slotForRole(token.user?.role)
      );
      queryClient.setQueryData<MeResponse>(ME_QUERY_KEY, {
        actor_type: token.actor_type,
        user: token.user ?? null,
        driver: token.driver ?? null,
      });
      setTokenPresent(true);
    },
    [queryClient]
  );

  const login = useCallback(
    async (payload: LoginRequest) => {
      const token = await authApi.login(payload);
      applyToken(token);
      return token;
    },
    [applyToken]
  );

  const driverLogin = useCallback(
    async (payload: DriverLoginRequest) => {
      const token = await authApi.driverLogin(payload);
      applyToken(token);
      return token;
    },
    [applyToken]
  );

  const logout = useCallback(async () => {
    try {
      // 감사 로그를 남기기 위한 호출. 실패해도 클라이언트 세션은 반드시 정리한다.
      await authApi.logout();
    } catch {
      /* 무시 */
    } finally {
      tokenStore.clear();
      setTokenPresent(false);
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
      queryClient.clear();
      // 화면에 남아 있던 조회 조건·쓰다 만 등록 폼도 함께 버린다. 같은 브라우저를 쓰는
      // 다음 사람이 앞사람이 보던 것을 물려받으면 안 된다.
      clearAllSticky();
      clearRecentScreens();
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => {
    const me = meQuery.data;
    const user = me?.actor_type === "user" ? (me.user ?? null) : null;
    const driver = me?.actor_type === "driver" ? (me.driver ?? null) : null;
    const role: AppRole | null = user ? user.role : driver ? "기사" : null;
    // 서버가 `role` 을 반드시 포함해 내려 주지만, 옛 응답이 캐시에 남아 있을 수 있어
    // 여기서도 대표 역할을 메운다 — 비면 그 계정의 포털이 통째로 닫힌다.
    const portalRoles: PortalRole[] = (() => {
      if (!user) return [];
      const picked = new Set<string>(user.portal_roles ?? []);
      if (PORTAL_ROLES.includes(user.role as PortalRole)) picked.add(user.role);
      return PORTAL_ROLES.filter((r) => picked.has(r));
    })();

    return {
      user,
      driver,
      role,
      portalRoles,
      displayName: user?.name ?? driver?.name ?? "",
      isAuthenticated: Boolean(user || driver),
      // 토큰은 있는데 아직 주체를 모르고 실패도 아니면 복원 중이다.
      isRestoring: tokenPresent && !me && !meQuery.isError,
      login,
      driverLogin,
      logout,
    };
  }, [
    meQuery.data,
    meQuery.isError,
    tokenPresent,
    login,
    driverLogin,
    logout,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth 는 AuthProvider 안에서만 사용할 수 있습니다.");
  }
  return ctx;
}
